/**
 * Status das imagens: hora da mais recente publicada pela NOAA, há quanto
 * tempo, e "sem conexão". Três formas: linha completa (painel), compacta
 * (duas linhas curtas) e em linha (dentro de um chip sobre o mapa).
 */
import clsx from 'clsx';
import { WifiOff } from 'lucide-react';
import { useCatalogStore } from '../goes/catalog';
import { getProductName } from '../config/products';
import { formatDateBrt, formatDateTimeBoth, formatDayTimeBrt, formatRelative, formatTimeBrt } from '../lib/time';
import { useUiStore } from '../state/ui';
import { ATRASO_NOAA_MS, mensagemAtrasoDaNoaa } from '../loop/autoRefresh';
import { useBaseProductId, useLatestTime, useNow } from './hooks';

export function LatestStatus(props: {
  variant?: 'full' | 'compact' | 'inline';
  className?: string;
}) {
  const variant = props.variant ?? 'full';
  const latest = useLatestTime();
  const base = useBaseProductId();
  const failed = useCatalogStore((s) => (base ? s.entries[base]?.status === 'error' : false));
  const online = useUiStore((s) => s.online);
  const now = useNow();
  // A NOAA parou de publicar (ver `ATRASO_NOAA_MS`): o selo diz isso, e não só "há 2 h".
  const stale = latest !== null && now - latest > ATRASO_NOAA_MS;
  // Parada de outro dia (o GeoColor de 20/10/2026 passou da meia-noite): "12:00" sozinho parecia de hoje.
  const quando = latest === null ? '' : formatDateBrt(latest) === formatDateBrt(now) ? formatTimeBrt(latest) : formatDayTimeBrt(latest);
  const produto = base ? getProductName(base) : null;
  const inline = variant === 'inline';
  const compact = variant === 'compact';

  return (
    <div
      className={clsx(
        'flex min-w-0 items-center gap-2 text-xs text-ink-200',
        !inline && 'border border-ink-700 bg-ink-850/80',
        compact && 'w-fit max-w-full rounded-xl px-2.5 py-1',
        variant === 'full' && 'rounded-full px-3 py-1',
        props.className,
      )}
      role="status"
      aria-live="polite"
      title={
        latest === null
          ? undefined
          : stale
            ? mensagemAtrasoDaNoaa(formatDateTimeBoth(latest), produto)
            : `Imagem mais recente: ${formatDateTimeBoth(latest)}. Atualização automática: imagens novas entram sozinhas.`
      }
    >
      {!online ? (
        <>
          <WifiOff className="size-3.5 text-yellow-300" aria-hidden />
          <span className="text-yellow-200">Sem conexão</span>
        </>
      ) : (
        <>
          <span className="relative flex size-2 shrink-0" aria-hidden>
            {/*
              O ponto pulsa só quando chega imagem nova (`key` reinicia a
              animação), e poucas vezes. Pulsando sem parar, o navegador
              recompunha a tela inteira — dez telas de mapa — 60 vezes por
              segundo, com o loop parado ou não: processador e bateria à toa.
            */}
            {!stale && latest !== null && (
              <span key={latest} className="pulso-imagem-nova absolute inline-flex size-full rounded-full bg-mata-400 opacity-60 motion-reduce:hidden" />
            )}
            <span
              className={clsx(
                'relative inline-flex size-2 rounded-full',
                stale || (failed && latest === null) ? 'bg-yellow-300' : 'bg-mata-400',
              )}
            />
          </span>
          {latest === null ? (
            <span className={failed ? 'text-yellow-200' : undefined}>
              {failed ? (variant === 'full' ? 'NOAA sem resposta' : 'Sem resposta') : variant === 'full' ? 'Buscando imagens…' : 'Buscando…'}
            </span>
          ) : inline ? (
            <span className="truncate tabular-nums" title={stale ? mensagemAtrasoDaNoaa(`${quando} (Brasília)`, produto) : `Imagem mais recente: ${quando} (Brasília)`}>
              <span className="font-medium text-white">{quando} · </span>
              {/* Parada da NOAA: o chip do desktop dizia só "há 3 h" em amarelo, e o portal parecia travado. */}
              <span className={stale ? 'text-yellow-200' : 'text-ink-300'}>
                {stale ? `NOAA atrasada · ${formatRelative(latest, now)}` : formatRelative(latest, now)}
              </span>
            </span>
          ) : compact ? (
            <span className="flex min-w-0 flex-col leading-tight" title={stale ? mensagemAtrasoDaNoaa(`${quando} (Brasília)`, produto) : `Imagem mais recente: ${quando} (Brasília)`}>
              <span className="font-medium text-white tabular-nums">{formatTimeBrt(latest)}</span>
              <span className={clsx('truncate text-[10px]', stale ? 'text-yellow-200' : 'text-ink-400')}>
                {stale ? `NOAA atrasada · ${formatRelative(latest, now)}` : formatRelative(latest, now)}
              </span>
            </span>
          ) : (
            <span className="truncate">
              <span className="text-ink-400">Mais recente: </span>
              <span className="text-ink-300">{formatDateBrt(latest).slice(0, 5)} </span>
              <span className="font-medium text-white tabular-nums">{formatTimeBrt(latest)}</span>
              <span className="text-ink-400"> (Brasília) · </span>
              <span className={stale ? 'text-yellow-200' : 'text-ink-400'}>
                {stale ? `NOAA sem imagem nova ${formatRelative(latest, now)}` : formatRelative(latest, now)}
              </span>
            </span>
          )}
        </>
      )}
    </div>
  );
}

/**
 * Para o relógio do topo do celular: a idade da imagem mais recente ("há 19 min") e o aviso que
 * vale para qualquer quadro (sem conexão, NOAA atrasada) — o que o `LatestStatus` dizia no chip.
 */
export function useIdadeDaImagem(): { idade: string | null; aviso: string | null } {
  const latest = useLatestTime();
  const online = useUiStore((s) => s.online);
  const now = useNow();
  if (!online) return { idade: null, aviso: 'sem conexão' };
  if (latest === null) return { idade: null, aviso: null };
  if (now - latest > ATRASO_NOAA_MS) return { idade: null, aviso: 'NOAA atrasada' };
  return { idade: formatRelative(latest, now), aviso: null };
}
